import * as React from 'react'
import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'

import NotFound from 'modules/core/components/NotFound'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import {
  FACTORIAL_APP,
  PROFESSIONALS_APP,
  trackIncidenceSentMessage,
} from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import incidenceHasMessage from 'modules/bookkeepers/lib/hasNewMessage'
import filesCollection from 'modules/bookkeepers/store/collections/files'
import { useIncidenceMessages } from 'modules/bookkeepers/store/collections/incidenceMessages'
import TabsSelector from 'modules/payroll_bundle/components/TabsSelector'
import useCreateIncidenceMessage from 'modules/payroll_bundle/hooks/useCreateIncidenceMessage'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { hasInHousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import { useUpdateIncidenceMessageStatus } from 'modules/payroll_bundle/hooks/useUpdateIncidenceMessageStatus'
import IncidenceMessageForm from 'modules/payroll_bundle/lib/forms/IncidenceMessageForm'

import MessageList from './_MessageList'
import NewMessage from './_NewMessage'

const Container = () => {
  const { id } = useParams<'id'>()
  const isBookkeeperView = useBookkeeperView()
  const incidenceId = String(id)
  const incidence = useCurrentIncidence()
  const [files, setFiles] = React.useState<File[]>([])
  const hasMessage = incidenceHasMessage(isBookkeeperView, incidence)
  const { update } = useUpdateIncidenceMessageStatus()
  const incidenceMessages = useIncidenceMessages()
  const { hasCompensations: hasCompensationsModule } = useHasCompensations()

  React.useEffect(() => {
    if (hasCompensationsModule && incidence?.name) {
      track(trackingEvents.compensations.checkDetailsInEmployeeUpdates, {
        update_type: incidence.name,
        tab: 'messages',
        application: isBookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
      })
    }
  }, [hasCompensationsModule, incidence?.name, isBookkeeperView])

  const messageForm = React.useRef(
    new IncidenceMessageForm({
      message: '',
      incidence_id: incidenceId,
      bookkeeper_view: isBookkeeperView,
      type: 'incidence',
    })
  ).current

  const messageField = messageForm.get('message')

  const { submitting, create } = useCreateIncidenceMessage({
    messageForm,
    files,
    filesCollection,
  })

  const handleFileChange = (files: File[]) => {
    setFiles(files)
  }

  const handleSubmit = () => {
    create()
    if (hasCompensationsModule) {
      track(trackingEvents.compensations.sendMessageInEmployeeUpdates, {
        application: isBookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
      })
    } else {
      trackIncidenceSentMessage(incidence, isBookkeeperView, files)
    }
    setFiles([])
  }

  useUnsafeEffect(() => {
    if (hasMessage && incidence) {
      const variables = {
        incidenceId: incidence?.id,
        hasMessage: false,
      }

      update(variables)
      incidence.hasMessage = false
    }
  }, [incidence, hasMessage, update])

  return (
    <>
      <TabsSelector
        selectedTab="messages"
        incidenceId={incidenceId}
        isBookkeeper={isBookkeeperView}
      />
      <Box overflowY="hidden">
        <MessageList messages={incidenceMessages.models} />

        <NewMessage
          submitting={submitting}
          messageField={messageField}
          onSubmit={handleSubmit}
          onFileChange={handleFileChange}
          files={files}
        />
      </Box>
    </>
  )
}

const IncidenceMessagePage = () => {
  if (hasInHousePayroll()) {
    return <NotFound />
  }

  return <Container />
}

export default IncidenceMessagePage
